/**
 * ⚡ TTFB: ذاكرة SSR قصيرة مع "stale-while-revalidate".
 *
 * السبب: بعض الصفحات العامّة (الرئيسيّة · الهدايا · من نحن · المنتج) كانت
 * تستعلم قاعدة البيانات قبل إرسال أوّل بايت، فيصل زمن أوّل بايت إلى ١.١–١.٨ث
 * بينما الصفحات بلا قاعدة بيانات ترسل في ٠.١٥ث.
 *
 * السلوك:
 * - الطلبات المتزامنة للمفتاح نفسه تشترك في قراءة واحدة.
 * - أوّل طلب في هذه النسخة من السيرفر: ينتظر بمهلة قصوى ثمّ يعيد الاحتياطيّ.
 *   إذا اكتملت القراءة لاحقاً أثناء حياة العملية، تُحفظ النتيجة الناجحة أيضاً.
 * - الطلبات التالية: تُخدَم من الذاكرة فوراً.
 * - بعد انتهاء العمر: تُخدَم القيمة القديمة فوراً ويُحدَّث في الخلفيّة.
 * - عند التأخّر أو الفشل: تُستخدم القيمة الاحتياطيّة بلا حبس الصفحة.
 */

type Entry<T> = { at: number; data: T };
type PendingEntry<T> = { at: number; promise: Promise<T> };

const store = new Map<string, Entry<unknown>>();
const pending = new Map<string, PendingEntry<unknown>>();

const TIMED_OUT = Symbol("ssr-cache-timeout");
// A stalled/cancelled read must not pin this key to the fallback forever.
// This bounds coalescing, independently of the caller's 600ms paint deadline.
const MAX_PENDING_MS = 30_000;

function activeRead<T>(key: string): PendingEntry<T> | undefined {
  const entry = pending.get(key) as PendingEntry<T> | undefined;
  if (entry && Date.now() - entry.at >= MAX_PENDING_MS) {
    pending.delete(key);
    return undefined;
  }
  return entry;
}

/** Public data only. Include every input that changes the result in the key. */
function fetchAndCache<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const existing = activeRead<T>(key);
  if (existing) return existing.promise;

  // Cache on completion of the read, not on completion of a caller's deadline.
  // A timed-out caller must not discard a later successful read or start a
  // duplicate request while that read is still in flight.
  const entry: PendingEntry<T> = {
    at: Date.now(),
    promise: Promise.resolve()
      .then(fetcher)
      .then((data) => {
        // A superseded slow read cannot overwrite the newer request's result.
        if (pending.get(key) === entry) store.set(key, { at: Date.now(), data });
        return data;
      })
      .finally(() => {
        if (pending.get(key) === entry) pending.delete(key);
      }),
  };
  pending.set(key, entry);
  return entry.promise;
}

export async function cachedSSR<T>(
  key: string,
  fetcher: () => Promise<T>,
  fallback: T,
  opts?: { ttlMs?: number; timeoutMs?: number },
): Promise<T> {
  const ttlMs = opts?.ttlMs ?? 600_000; // ١٠ دقائق
  const timeoutMs = opts?.timeoutMs ?? 600;
  const hit = store.get(key) as Entry<T> | undefined;

  if (hit) {
    const stale = Date.now() - hit.at >= ttlMs;
    if (stale && !activeRead(key)) {
      // تحديث في الخلفيّة — لا ينتظره أوّل بايت.
      void fetchAndCache(key, fetcher).catch(() => {
        hit.at = Date.now(); // لا نعيد المحاولة مع كلّ طلب عند الفشل
      });
    }
    return hit.data;
  }

  let deadline: ReturnType<typeof setTimeout> | undefined;
  try {
    const result = await Promise.race<T | typeof TIMED_OUT>([
      fetchAndCache(key, fetcher),
      new Promise<typeof TIMED_OUT>((resolve) => {
        deadline = setTimeout(() => resolve(TIMED_OUT), timeoutMs);
      }),
    ]);
    if (result === TIMED_OUT) return fallback;
    return result;
  } catch {
    return fallback;
  } finally {
    if (deadline !== undefined) clearTimeout(deadline);
  }
}
